import { JRCIconButton } from 'components/JRCIconButton/JRCIconButton';
import { useCancelOnUnmount } from 'hooks/useCancelOnUnmount';
import { deepEqual } from 'jamespot-user-api';
import React, {
    ChangeEvent,
    ForwardedRef,
    forwardRef,
    KeyboardEvent,
    MouseEvent,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { FieldValues } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { useDebounce } from '../../../../hooks/UseDebounce';
import { scrollIntoView } from '../../../../utils/utils.document';
import { JRCTypography } from '../../../JRCTypography/JRCTypography';
import { StyledSelect } from '../JRCStyledInput';
import * as Components from './JRCInputSelect.style';
import { NativeSelectProps } from './JRCInputSelect.types';
import * as utils from './JRCInputSelect.utils';

const DescriptionWrapper = styled.div`
    margin-bottom: ${({ theme }) => theme.space.sm}px;
`;

type InputSelectProps<T extends FieldValues> = Pick<Partial<NativeSelectProps<T>>, 'ref'> &
    Omit<NativeSelectProps<T>, 'ref'> & {
        allowCustomOptions?: boolean;
        loading?: boolean;
        minWidth?: string | false;
    };

const JRCInputSelectRawElement = function <
    T extends FieldValues = FieldValues,
    U extends { resetOptions?: boolean } = any,
>(
    {
        asyncPromise,
        searchable,
        showAsyncResultOnEmptySearch,
        displayOption = true,
        displayIndicator = true,
        iconBefore,
        value,
        allowCustomOptions,
        limit,
        ...props
    }: InputSelectProps<T>,
    ref: ForwardedRef<HTMLInputElement>,
) {
    const intl = useIntl();

    const { multiple, readOnly, onChange, required, getGroup: _getGroup } = props;
    const getLabel = (option: U) => utils.get(option, props.getLabel);
    const getValue = (option: U) => utils.get(option, props.getValue);
    const getSearch = (option: U) => utils.get(option, props.getSearch || props.getLabel);
    const getGroup = useCallback((option: U) => utils.get(option, _getGroup), [_getGroup]);

    const [cancelOnUnmount] = useCancelOnUnmount();
    const [search, setSearch] = useState<string>('');
    const debouncedSearch = useDebounce(search);
    const [open, setOpen] = useState(false);
    const [shouldScroll, setShouldScroll] = useState(false);
    const [focusIndex, setFocusIndex] = useState(0);
    const [loading, setLoading] = useState(props.loading || false);
    const instantiated = useRef(false);
    const [noOptionMessage, setNoOptionMessage] = useState('');

    const inputRef = useRef<HTMLInputElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const [options, setOptions] = useState(props.options || []);

    const iconBeforeComponent = iconBefore ? (
        iconBefore.onClick ? (
            <Components.IconBefore
                as={JRCIconButton}
                icon={iconBefore.icon}
                size="medium"
                color={iconBefore.color}
                onClick={iconBefore.onClick}
                left={5}
                top={5}
                height={30}
                width={30}
                name={iconBefore.icon}
            />
        ) : (
            <Components.IconBefore name={iconBefore.icon} size={20} color={iconBefore.color} left={6} />
        )
    ) : null;

    const memoizedOptions = useMemo(
        () => {
            if (searchable) {
                const isSelected = options.filter((option) => utils.isOrIncludes(value, option));
                const searchMatched =
                    value?.label?.toLowerCase?.()?.includes(search.toLowerCase()) && !!isSelected.length;
                return options.filter(
                    (option) => !search || searchMatched || new RegExp(search, 'i').test(getSearch(option)),
                );
            }
            return [...options.filter((o) => !o.custom), ...options.filter((o) => o.custom)];
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [options, search],
    );

    useEffect(() => {
        if (props.options) setOptions(props.options);
    }, [props.options]);

    useEffect(() => {
        if (!props.multiple) {
            setSearch(value === null || value === undefined ? '' : (getSearch(value) as string));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => {
        if (!searchable && instantiated.current) {
            setLoading(props.loading || false);
        }

        if (asyncPromise && (showAsyncResultOnEmptySearch || !!debouncedSearch)) {
            setLoading(true);
            instantiated.current = true;
            cancelOnUnmount(asyncPromise(debouncedSearch))
                .then((res) => {
                    if (!allowCustomOptions || res.some((r) => r.title === debouncedSearch) || !debouncedSearch) {
                        setOptions(res);
                        setLoading(props.loading || false);
                    } else {
                        setOptions([{ title: debouncedSearch, newOption: true }, ...res]);
                        setLoading(props.loading || false);
                    }
                })
                .catch((err) => {
                    if (!err.isCanceled) {
                        setLoading(props.loading || false);
                    }
                });
        } else {
            setLoading(props.loading || false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, props.loading]);

    useEffect(() => {
        if (!asyncPromise && !memoizedOptions.length) {
            setNoOptionMessage('GLOBAL_No_Result');
        } else if (asyncPromise && !memoizedOptions.length && loading) {
            setNoOptionMessage('GLOBAL_Search_Loading');
        } else if (
            asyncPromise &&
            !search &&
            ((showAsyncResultOnEmptySearch && memoizedOptions.length) || !showAsyncResultOnEmptySearch)
        ) {
            setNoOptionMessage('GLOBAL_Start_typing');
        } else if (asyncPromise && !memoizedOptions.length && !loading) {
            setNoOptionMessage('GLOBAL_No_Result');
        } else {
            setNoOptionMessage('');
        }
    }, [asyncPromise, memoizedOptions, loading, showAsyncResultOnEmptySearch, search]);

    /*
     * Ref of the selected option
     * based on https://github.com/JedWatson/react-select/blob/c9636332990d4bec2e6544e143dde2720d76061c/packages/react-select/src/Select.tsx#L736-L744
     */
    let selectedOptionRef: HTMLButtonElement | null = null;

    function selectedOptionRefCallback(ref: HTMLButtonElement) {
        selectedOptionRef = ref;
        if (shouldScroll) {
            setShouldScroll(false);
            if (menuRef.current && selectedOptionRef) {
                scrollIntoView(menuRef.current, selectedOptionRef);
            }
        }
    }

    // Hack to use .focus() outside of this component while being able to access the ref within this component
    useImperativeHandle(
        ref,
        () =>
            ({
                focus: () => {
                    inputRef?.current?.focus();
                },
            } as any),
    );

    const onClick = useCallback(
        (e: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLInputElement> | undefined, option: U) => {
            if (readOnly) {
                return;
            }

            if (multiple) {
                if (option.resetOptions) {
                    onChange(
                        value && Array.isArray(value) && (value as any[]).some((v) => deepEqual(v, option))
                            ? []
                            : [option],
                    );
                } else {
                    onChange(
                        value && Array.isArray(value) && (value as any[]).some((v) => deepEqual(v, option))
                            ? (value as any[]).filter((v) => !deepEqual(v, option))
                            : [...(Array.isArray(value) ? value : []), option],
                    );
                }
                if (asyncPromise && searchable) setSearch('');
            } else {
                const isEqual = deepEqual(value, option);
                if (isEqual && !required) {
                    onChange('');
                } else if (!isEqual) {
                    onChange(option);
                }
            }

            setOpen(false);

            // keep focus inside the input
            inputRef?.current?.focus();
            // when pressing enter to select an option, prevent form submission
            if (e) e.preventDefault();
            return;
        },
        [asyncPromise, multiple, onChange, readOnly, required, searchable, value],
    );

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (searchable) {
            setSearch(e.target.value !== null && e.target.value !== undefined ? e.target.value : '');
        }
    };

    const handleKey = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (readOnly) return;

            if (e.key === 'ArrowDown') {
                setFocusIndex(open ? (focusIndex + 1) % memoizedOptions.length : 0);
                setShouldScroll(true);
                setOpen(true);
            } else if (e.key === 'ArrowUp') {
                setFocusIndex(open && focusIndex > 0 ? focusIndex - 1 : memoizedOptions.length - 1);
                setShouldScroll(true);
                setOpen(true);
            } else if (e.key === 'Enter' && (focusIndex !== undefined || allowCustomOptions)) {
                e.preventDefault();
                const focusedOption = memoizedOptions
                    .map((el) => ({ el, group: getGroup(el) }))
                    .sort((el1, el2) => (el1.group === el2.group ? 0 : el1.group < el2.group ? -1 : 1))
                    .map((v) => v.el)[focusIndex];
                if (focusedOption) {
                    onClick(e, focusedOption);
                    if (!multiple) setOpen(false);
                } else if (allowCustomOptions && memoizedOptions.length > 0) {
                    onClick(e, memoizedOptions[0]);
                    if (!multiple) setOpen(false);
                }
            } else if (e.key === 'Tab' || e.key === 'Escape') {
                setOpen(false);
            } else if (!multiple && value && ['Backspace', 'Delete'].includes(e.key)) {
                onChange('');
                setOpen(true);
            }
        },
        [readOnly, focusIndex, allowCustomOptions, multiple, value, open, memoizedOptions, getGroup, onClick, onChange],
    );

    const timesIndicator = !props.hideDeleteIcon && !props.multiple && !props.required && !props.readOnly && value;
    const dropdownIndicator = displayIndicator && !props.readOnly;
    const hasIconAfter = timesIndicator || dropdownIndicator;

    const hasReachedLimit = !!value && !!limit && value.length === limit;

    return (
        <>
            {props.multiple && props.hideSelectedOption !== true && value && Array.isArray(value) && (
                <Components.SelectedOptionWrapper>
                    {(value as Array<any>).map((v, index) => (
                        <Components.SelectedOption
                            key={`${v.title.replace(/[^a-z0-9]/gi, '')}-${index}`}
                            onClick={
                                props.readOnly
                                    ? undefined
                                    : () => {
                                          props.onChange(
                                              (value as any[]).filter((initialValue) => !deepEqual(initialValue, v)),
                                          );
                                      }
                            }
                            readOnly={props.readOnly}>
                            {props?.components?.SelectedOption ? (
                                <props.components.SelectedOption option={v} />
                            ) : (
                                <Components.JRCDefaultSelectedOption>
                                    <Components.TimesIcon />
                                    {getValue(v)}
                                </Components.JRCDefaultSelectedOption>
                            )}
                        </Components.SelectedOption>
                    ))}
                </Components.SelectedOptionWrapper>
            )}
            <Components.RelativeClickAwayListener onClickAway={() => setOpen(false)} className={props.className}>
                {iconBeforeComponent || null}
                <StyledSelect
                    hasIconBefore={Boolean(iconBeforeComponent)}
                    hasIconAfter={hasIconAfter}
                    ref={inputRef}
                    id={props.htmlFor}
                    disabled={props.disabled || hasReachedLimit}
                    readOnly={props.readOnly}
                    placeholder={
                        hasReachedLimit
                            ? intl.formatMessage({ id: 'INPUT_SELECT_Has_Reached_Limit' }, { limit })
                            : props.placeholder
                    }
                    searchable={searchable}
                    value={search}
                    dataCy={props.dataCy}
                    onChange={handleChange}
                    onKeyDown={handleKey}
                    onClick={
                        props.readOnly
                            ? undefined
                            : () => {
                                  setOpen((prev) => !prev);
                                  setShouldScroll((prev) => !prev);
                              }
                    }
                    sizeVariant={props.size}
                />
                {timesIndicator ? <Components.TimesIndicator onClick={() => props.onChange('')} /> : null}
                {dropdownIndicator ? (
                    <Components.DropdownIndicator
                        loading={loading}
                        disabled={props.disabled}
                        onClick={() => setOpen((open) => !open)}
                        open={open && (!asyncPromise || !searchable || showAsyncResultOnEmptySearch || !!search)}
                        sizeVariant={props.size}
                    />
                ) : null}
                {displayOption && open && (noOptionMessage || memoizedOptions.length) && (
                    <Components.OptionMenu ref={menuRef} sizeVariant={props.size} minWidth={false}>
                        {noOptionMessage && (
                            <Components.NoOption>
                                <FormattedMessage id={noOptionMessage} />
                            </Components.NoOption>
                        )}
                        {!props.group &&
                            memoizedOptions.map((option, index) => (
                                <Components.OptionWrapper
                                    className={option.custom ? 'custom' : undefined}
                                    key={index}
                                    ref={index === focusIndex ? selectedOptionRefCallback : undefined}
                                    onClick={(e) => onClick(e, option)}
                                    isFocused={index === focusIndex}
                                    data-cy={props.dataCy && `${props.dataCy}-option-${getValue(option)}`}
                                    isSelected={utils.isOrIncludes(value, option)}>
                                    {props?.components?.Option ? (
                                        <props.components.Option option={option} />
                                    ) : (
                                        <>
                                            {getLabel(option).toString()}
                                            {props.getDescription && (
                                                <DescriptionWrapper>
                                                    <JRCTypography size={'s'} color={'grey3'}>
                                                        {props.getDescription(option)}
                                                    </JRCTypography>
                                                </DescriptionWrapper>
                                            )}
                                        </>
                                    )}
                                </Components.OptionWrapper>
                            ))}
                        {props.group &&
                            utils.group(memoizedOptions, getGroup).map((group, groupIndex) => (
                                <Components.OptionGroup key={group.label}>
                                    <Components.OptionGroupHeader>{group.label}</Components.OptionGroupHeader>
                                    <Components.OptionGroupValues>
                                        {group.options.map((option) => (
                                            <Components.OptionWrapper
                                                onClick={(e) => onClick(e, option)}
                                                data-cy={
                                                    props.dataCy &&
                                                    `${props.dataCy}-group-${groupIndex}-option-${getValue(option)}`
                                                }
                                                key={option._index}
                                                ref={
                                                    option._index === focusIndex ? selectedOptionRefCallback : undefined
                                                }
                                                isFocused={option._index === focusIndex}
                                                isSelected={utils.isOrIncludes(value, option)}>
                                                {props?.components?.Option ? (
                                                    <props.components.Option option={option} />
                                                ) : (
                                                    getLabel(option).toString()
                                                )}
                                            </Components.OptionWrapper>
                                        ))}
                                    </Components.OptionGroupValues>
                                </Components.OptionGroup>
                            ))}
                    </Components.OptionMenu>
                )}
            </Components.RelativeClickAwayListener>
        </>
    );
};
export const JRCInputSelectRaw = forwardRef(JRCInputSelectRawElement) as unknown as <
    T extends FieldValues = FieldValues,
    U = any,
>(
    props: Pick<Partial<NativeSelectProps<T, U>>, 'ref'> & Omit<NativeSelectProps<T, U>, 'ref'>,
) => JSX.Element;
